/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { defaultValue } from './ClientSideExcelUpload';
import { ItemType } from 'componentregistry/registry/ComponentRegistry';

const ClientSideExcelUploadManager: ComponentManagerFn<
  'ClientSideExcelUpload'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'ClientSideExcelUpload'>(
      platform,
      'ClientSideExcelUpload',
    ),
    type: 'ClientSideExcelUpload',
    name: 'Client Side Excel/CSV Upload',
    isChildAllowed: (page, item, childType) =>
      (['TriggerAfterUpload'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      defaultFunction: {
        label: 'Perform Default Callback Function',
        type: 'controllerFunction',
        alwaysShow: true,
        helpText: `Use this function to populate default values.

This function is called before the data is validated against the datasource.

\`\`\`javascript
import { NewRow, MyDataSource } from "@cloudio-saas/datasource-types";
import type { BaseFunctionProps } from 'cloudio';
import ulid from 'ulid';

function performDefault({ records, fileName }: BaseFunctionProps & { records: NewRow<MyDataSource>[], fileName: string }) {
  const loadUid = ulid();
  records.forEach((record) => {
      for (const [key, value] of Object.entries(record)) {
          if (typeof value === 'string' && value.charAt(0) === '\\'') {
              record[key] = value.substring(1);
          }
      }
      record.loadUid = loadUid;
      record.statusCode = 'P';
  });
  return records;
}
\`\`\`
`,
      },
      callbackFunction: {
        label: 'On Load Callback Function',
        type: 'controllerFunction',
        alwaysShow: true,
        helpText: `Use this function to perform any additional processing.

\`\`\`javascript
import { NewRow, MyDataSource } from "@cloudio-saas/datasource-types";
import type { BaseFunctionProps } from 'cloudio';

function onLoad({ platform, records, fileName }: BaseFunctionProps & { records: NewRow<MyDataSource>[], fileName: string }) {
  const rows = records.filter((record) => record.someAttr === 'someValue');
  const removed = records.length - rows.length;
  if (removed) {
      platform.showWarning(removed === 1 ? \`One record was ignored due to...!\` : \`\${removed} records were ignored due to...!\`);
  }
  return rows;
}
\`\`\`
`,
      },
      alias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: ['DataStore'],
      },
      maxRows: {
        label: 'Max Rows Allowed',
        type: 'numberInput',
        required: true,
      },
      dateFormat: {
        label: 'Date Format',
        type: 'string',
        required: true,
        helpText:
          'Date format to be used for parsing dates. Refer to [date-fns](https://date-fns.org/v2.29.3/docs/parse) for more details.',
      },
      dateTimeFormat: {
        label: 'Date Time Format',
        type: 'string',
        required: true,
        helpText:
          'Date time format to be used for parsing date times. Refer to [date-fns](https://date-fns.org/v2.29.3/docs/parse) for more details.',
      },
      headerMappings: {
        label: 'Header Mappings',
        type: 'json',
        required: false,
        sampleData: `[
          {
            "header":  "Sample Header ",
            "attribute": "Sample Attribute Code " 
          }
      ]`,
      },
    },
    getDisplayText: (item) => item.props.callbackFunction,
    category: 'Forms',
    icon: { iconName: 'upload' },
    description: 'Client Side Excel/CSV file processing',
  };
};

export default ClientSideExcelUploadManager;
